import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { listAnnouncements } from '@ebalance/proto/bffuser/v1/announcement_service-AnnouncementService_connectquery'
import {
  ListAnnouncementsRequestSchema,
  type ListAnnouncementsResponse,
} from '@ebalance/proto/bffuser/v1/announcement_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

/**
 * BFF AnnouncementService.ListAnnouncements を取得する hook。
 * 告知は全組織共通のため request は空 (organization_id を持たない)。
 * 一覧 page とサイドメニューの未読ドットで cache を共有する。
 *
 * @param options 未指定の key は渡さず QueryClient 既定に従う (`undefined` を
 *   渡すと既定値を上書きしてしまうため)。ただし `throwOnError` の未指定は
 *   「data 未取得の間だけ throw」にする。サイドメニューのように告知が付帯情報で
 *   しかない呼び出し元は `throwOnError: false` を渡す (throw すると RPC 1 本の
 *   失敗で全画面エラーになる)。
 */
export function useListAnnouncements(options?: {
  throwOnError?: boolean
  refetchOnWindowFocus?: boolean
  staleTime?: number
}): UseQueryResult<ListAnnouncementsResponse, ConnectError> {
  return useQuery(
    listAnnouncements,
    create(ListAnnouncementsRequestSchema, {}),
    {
      // サイドメニューの window focus 再取得 (cache 共有) が一覧表示中に失敗しても、
      // 表示できている一覧を全画面エラーに差し替えない。初回ロードの失敗は従来どおり
      // throw する (`useListWorkflowCases` の背景再取得と同じ扱い)。
      throwOnError:
        options?.throwOnError ??
        ((_error, query) => query.state.data === undefined),
      ...(options?.refetchOnWindowFocus !== undefined
        ? { refetchOnWindowFocus: options.refetchOnWindowFocus }
        : {}),
      ...(options?.staleTime !== undefined
        ? { staleTime: options.staleTime }
        : {}),
    },
  )
}
